import React from 'react';
import * as Sentry from '@sentry/react';
import packageJson from '../package.json';

const dsn = process.env.REACT_APP_SENTRY_DSN;
const enabled = process.env.NODE_ENV !== 'test' && Boolean(dsn);

// Query params that must never reach telemetry. The impersonation handoff token
// (M7-365) rides in `/impersonate?token=...`; without scrubbing, Sentry's default
// navigation breadcrumbs and `request.url` would capture that single-use signed
// token and attach it to any later error event. Redact it from every URL Sentry
// records. Exported for direct unit testing (Sentry.init is disabled under test).
const SENSITIVE_QUERY_PARAMS = ['token'];

export function scrubUrl(url) {
    if (typeof url !== 'string' || !url) {
        return url;
    }
    try {
        const parsed = new URL(url, window.location.origin);
        let changed = false;
        SENSITIVE_QUERY_PARAMS.forEach(param => {
            if (parsed.searchParams.has(param)) {
                parsed.searchParams.set(param, '[REDACTED]');
                changed = true;
            }
        });
        return changed ? parsed.toString() : url;
    } catch (e) {
        // Not an absolute/parseable URL — leave it untouched rather than risk
        // dropping useful context.
        return url;
    }
}

function scrubBreadcrumbUrls(breadcrumb) {
    if (!breadcrumb || !breadcrumb.data) {
        return breadcrumb;
    }
    ['url', 'from', 'to'].forEach(key => {
        if (typeof breadcrumb.data[key] === 'string') {
            breadcrumb.data[key] = scrubUrl(breadcrumb.data[key]);
        }
    });
    return breadcrumb;
}

// Frontend error reporting via Sentry, matching the backend (sentry-ruby) and
// mobile (@sentry/react-native) under the same Sentry org. Initialized only when
// a DSN is configured (and outside tests); with no DSN this is a no-op, so
// dev/local builds stay silent. Set REACT_APP_SENTRY_DSN in the build env to enable.
if (enabled) {
    Sentry.init({
        dsn,
        environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,
        // Tie events to a build. Reuse the existing build identifier
        // (package.json buildDate, written by scripts/generateBuildVersion.js)
        // so each deploy is a distinct Sentry release without extra plumbing.
        release: process.env.REACT_APP_VERSION || `minute7-frontend@${packageJson.buildDate}`,
        // Strip the impersonation token from breadcrumb URLs (navigation captures
        // from/to) and from the event's request.url + attached breadcrumbs.
        beforeBreadcrumb(breadcrumb) {
            return scrubBreadcrumbUrls(breadcrumb);
        },
        beforeSend(event) {
            if (event.request && typeof event.request.url === 'string') {
                event.request.url = scrubUrl(event.request.url);
            }
            if (Array.isArray(event.breadcrumbs)) {
                event.breadcrumbs.forEach(scrubBreadcrumbUrls);
            }
            return event;
        },
    });
}

const Fallback = () => (
    <div className="errorBoundaryFallback" role="alert">
        Something went wrong. Please refresh the page.
    </div>
);

// Sentry's boundary (reports React render crashes) when enabled; otherwise a
// passthrough so the tree renders unchanged in tests / no-DSN builds.
export const ErrorBoundary = enabled
    ? ({ children }) => (
          <Sentry.ErrorBoundary fallback={<Fallback />}>{children}</Sentry.ErrorBoundary>
      )
    : ({ children }) => children;

export function setUser(user) {
    if (enabled) Sentry.setUser(user);
}

export function captureException(error, context) {
    if (enabled) Sentry.captureException(error, context);
}
